import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import React, { useState } from 'react';
import {
  useDeleteEventdetector,
  useDeleteGroundingConfiguration,
} from '@ais-ui/api';
import { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { mutateCallbacks } from '../../../utils/mutateCallbacks';
import { useLocalization } from '@ais-ui/locale-management';
import { GroundingConfigurationSchema } from '@ais-ui/api-spec/grounding';

interface DeleteDetectorProps {
  detector: EventdetectorDefinition | GroundingConfigurationSchema;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export function DeleteDetector({
  detector,
  showDialog,
  setShowDialog,
}: DeleteDetectorProps) {
  const { messages } = useLocalization();
  const { mutate } = useDeleteEventdetector();
  const { mutate: mutateGrounding } = useDeleteGroundingConfiguration();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();

    setIsSubmitting(true);

    const detectorName = detector.name;

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'delete',
      'detector',
      setIsSubmitting,
      setShowDialog
    );

    if (!detector.id) {
      toast({
        className: 'delete-detector-error-toast',
        title: 'Error!',
        description: `${messages.eventsDeleteDetectorErrorToastDescription} ${detectorName}`,
        variant: 'destructive',
      });
      setShowDialog(false);
      return;
    }
    if (detector.type === 'grounding') {
      mutateGrounding({ id: detector.id as number }, callBacks);
    } else {
      mutate(detector.id as string, callBacks);
    }
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent id="mutate-detector-dialog">
        <DialogHeader>
          <DialogTitle>{messages.eventdetectorsDeleteTitle}</DialogTitle>
          <DialogDescription>
            {messages.eventdetectorsDeleteWarning}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] 4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.eventdetectorsDeleteNameLabel}
            </Label>
            <Label htmlFor="detector-name">{detector.name}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={isSubmitting}
            >
              {messages.genericCancel}
            </Button>
            <Button
              variant="destructive"
              id="detector-confirm-delete-button"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? messages.genericDeleting
                : messages.genericDelete}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
